/**
 * The party's numbers (ADR-101): its size, its timers and its limits. Each is the knob a later change turns.
 */

/**
 * The most characters seated in one party, the player included (decisions 1 and 11).
 */
export const PARTY_SIZE = 6;

/**
 * How long an invite stays pending, in milliseconds (decision 4).
 */
export const PARTY_INVITE_MS = 30_000;

/**
 * How long a disconnected member's seat is kept, in milliseconds (decision 12).
 */
export const PARTY_SEAT_MS = 300_000;

/**
 * How long a disconnected leader keeps the lead, in milliseconds, before it passes (decisions 9 and 12, the author's
 * report of 2026-10-28). It outlasts the page's longest fresh background reconnect run (P1.9-T32): the waits of its
 * `RECONNECT_ATTEMPTS` (5) at their ceilings, 2 + 4 + 8 + 16 + 16 = 46 seconds, plus four failed attempts each hanging
 * to `JOIN_TIMEOUT_MS` (10 seconds), 86 seconds in all, which `reconnect.test.ts` holds against this value.
 */
export const PARTY_LEAD_GRACE_MS = 90_000;

/**
 * How long a truce lasts after a parting, in milliseconds (decision 10).
 */
export const PARTY_TRUCE_MS = 300_000;

/**
 * The most live truces one character holds (decision 10).
 */
export const PARTY_TRUCES_MAX = 25;

/**
 * The most invites pending to one character and from one party (decision 4.6).
 */
export const PARTY_PENDING_INVITES = 5;

/**
 * The most prompts one character is offered in a minute, from anyone (decision 4.6).
 */
export const PARTY_PROMPTS_PER_MINUTE = 6;

/**
 * How long an account is refused another invite to a character after a decline, a lapse or a withdrawal by the
 * inviter's leaving, in milliseconds (decision 4.5).
 */
export const PARTY_DECLINE_MS = 300_000;

/**
 * The invites one account sends in a minute (decision 29).
 */
export const PARTY_INVITES_PER_MINUTE = 10;

/**
 * The party requests a connection sends in a second, one window shared by the six (decision 29).
 */
export const PARTY_REQUESTS_PER_SECOND = 5;

/**
 * The values of a character's "Party invitations" setting, the default first (decision 6).
 */
export const PARTY_INVITATIONS = ["map", "friends", "friendsOfFriends", "nobody"] as const;
export type PartyInvitations = (typeof PARTY_INVITATIONS)[number];

/**
 * The longest map id a `party` member carries, in characters (PROTOCOL.md 6.70.0).
 */
export const PARTY_MAP_CHARS = 64;
